import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createWallet,
  fetchDepositMe,
  fetchIrrBalance,
  fetchNmcBalances,
  importWallet,
  listWallets,
  nextWalletLabel,
  scannerAddressUrl,
  type NmcBalances,
  type WalletItem,
} from "@/api/wallets";
import { fetchMarketPrices, fetchSpotFx } from "@/api/market";
import { IrrCtocDeposit } from "@/modules/wallet/IrrCtocDeposit";
import { DepositCoin } from "@/modules/wallet/DepositCoin";
import { WithdrawCoin } from "@/modules/wallet/WithdrawCoin";
import { errorMessage } from "@/api/auth";
import { useSession } from "@/session/SessionContext";
import { useToast } from "@/toast/ToastContext";
import { ensureDeviceUnlock } from "@/security/deviceLock";
import type { NetworkEnv } from "@/network/env";
import type { AppTab } from "@/modules/shell/tabs";
import { ChipTab } from "@/ui/wx";
import { fmtAmount, fmtUsd } from "@/lib/format";

function normalizePinDigits(v: string): string {
  return v.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))).replace(/\D/g, "");
}

function normalizePinLetters(v: string): string {
  return v.replace(/[^a-zA-Z]/g, "").toUpperCase();
}

function num(v: unknown): number {
  const n = Number(String(v ?? "").replace(/,/g, "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

type AssetRow = {
  key: string;
  name: string;
  qty: string;
  valueUsd: number;
  icon: string;
};

export function WalletsPanel({
  networkEnv,
  onJump,
}: {
  networkEnv: NetworkEnv;
  onJump?: (t: AppTab) => void;
}) {
  const { token, session } = useSession();
  const toast = useToast();
  const [items, setItems] = useState<WalletItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<"none" | "create" | "import">("none");
  const [pinDigits, setPinDigits] = useState("");
  const [pinLetters, setPinLetters] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [usdtRaw, setUsdtRaw] = useState(0);
  const [irrRaw, setIrrRaw] = useState(0);
  const [fxIrt, setFxIrt] = useState(0);
  const [nmcMap, setNmcMap] = useState<Record<string, NmcBalances>>({});
  const [pxMap, setPxMap] = useState<Record<string, number>>({});
  const [showIrrCtoc, setShowIrrCtoc] = useState(false);
  const [flow, setFlow] = useState<"none" | "deposit" | "withdraw">("none");
  const [hidden, setHidden] = useState(false);
  const [unit, setUnit] = useState<"irt" | "usdt">("irt");
  const [hideDust, setHideDust] = useState(true);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const out = await listWallets(token, networkEnv);
      const list = out.items || [];
      setItems(list);

      try {
        const dep = await fetchDepositMe(token);
        setUsdtRaw(num(dep.balanceUsdt));
      } catch {
        setUsdtRaw(0);
      }
      try {
        const ib = await fetchIrrBalance(token);
        setIrrRaw(num(ib.balanceIrr));
      } catch {
        setIrrRaw(0);
      }
      try {
        const fx = await fetchSpotFx();
        setFxIrt(num(fx.usdIrt));
      } catch {
        /* keep */
      }
      try {
        const items = await fetchMarketPrices();
        const m: Record<string, number> = {};
        for (const it of items || []) {
          const sym = String(it.symbol || "").toUpperCase();
          const n = num(it.priceUsd);
          if (sym && n > 0) {
            m[sym] = n;
            m[`${String(it.networkCode || "").toLowerCase()}:${sym}`] = n;
          }
        }
        setPxMap(m);
      } catch {
        /* keep */
      }

      const nmcWallets = list.filter((w) => String(w.chain).toLowerCase() === "nmc");
      const next: Record<string, NmcBalances> = {};
      await Promise.all(
        nmcWallets.map(async (w) => {
          try {
            next[w.address.toLowerCase()] = await fetchNmcBalances(token, w.address);
          } catch {
            next[w.address.toLowerCase()] = { tokens: {} };
          }
        })
      );
      setNmcMap(next);
    } catch (e) {
      toast.err(errorMessage(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, networkEnv, toast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (form !== "create" || !token) return;
    nextWalletLabel(token)
      .then(setLabel)
      .catch(() => setLabel(""));
  }, [form, token]);

  const assets: AssetRow[] = useMemo(() => {
    const rows: AssetRow[] = [
      {
        key: "usdt",
        name: "تتر (USDT)",
        qty: `${fmtAmount(usdtRaw)} USDT`,
        valueUsd: usdtRaw,
        icon: "T",
      },
      {
        key: "irr",
        name: "تومان (IRR)",
        qty: `${fmtAmount(irrRaw, { integer: true })} IRR`,
        valueUsd: fxIrt > 0 ? irrRaw / fxIrt : 0,
        icon: "﷼",
      },
    ];
    for (const w of items) {
      const chain = String(w.chain).toLowerCase();
      if (chain === "nmc") {
        const tokens = nmcMap[w.address.toLowerCase()]?.tokens || {};
        for (const [sym, row] of Object.entries(tokens)) {
          const bal = num(row.balance);
          const symU = sym.toUpperCase();
          const metalKey = symU.startsWith("N") ? symU : `N${symU}`;
          const px =
            pxMap[symU] ||
            pxMap[`nmc:${symU}`] ||
            pxMap[metalKey] ||
            pxMap[`nmc:${metalKey}`] ||
            0;
          rows.push({
            key: `${w.address}:${sym}`,
            name: `${sym} · NMC`,
            qty: fmtAmount(row.balance, { integer: true }),
            valueUsd: bal * px,
            icon: sym.slice(0, 1),
          });
        }
      } else {
        rows.push({
          key: `${w.id}-${w.chain}`,
          name: w.label || w.chain,
          qty: w.address.slice(0, 10) + "…",
          valueUsd: 0,
          icon: String(w.chain).slice(0, 1).toUpperCase(),
        });
      }
    }
    if (!hideDust) return rows;
    return rows.filter((r) => {
      if (r.key === "usdt") return usdtRaw >= 0.01;
      if (r.key === "irr") return irrRaw >= 1;
      return true;
    });
  }, [usdtRaw, irrRaw, fxIrt, items, nmcMap, hideDust, pxMap]);

  const metalsUsd = assets.reduce((s, r) => {
    if (r.key === "usdt" || r.key === "irr") return s;
    return s + (Number.isFinite(r.valueUsd) ? r.valueUsd : 0);
  }, 0);
  const totalUsd = usdtRaw + (fxIrt > 0 ? irrRaw / fxIrt : 0) + metalsUsd;
  const totalIrt = fxIrt > 0 ? totalUsd * fxIrt : irrRaw;
  const displayTotal = hidden
    ? "••••••"
    : unit === "irt"
      ? Math.round(totalIrt).toLocaleString("en-US")
      : totalUsd.toLocaleString("en-US", { maximumFractionDigits: 2 });

  async function onCreate() {
    if (!token) return;
    setBusy(true);
    try {
      await ensureDeviceUnlock();
      const d = normalizePinDigits(pinDigits);
      const l = normalizePinLetters(pinLetters);
      if (!/^\d{16}$/.test(d) || !/^[A-Z]{16}$/.test(l)) throw new Error("PIN");
      await createWallet(token, {
        pinDigits: d,
        pinLetters: l,
        networkEnv,
        label: label || undefined,
      });
      toast.ok("ساخت ولت");
      setPinDigits("");
      setPinLetters("");
      setForm("none");
      await refresh();
    } catch (e) {
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onImport() {
    if (!token) return;
    setBusy(true);
    try {
      await ensureDeviceUnlock();
      const d = normalizePinDigits(pinDigits);
      const l = normalizePinLetters(pinLetters);
      if (!/^\d{16}$/.test(d) || !/^[A-Z]{16}$/.test(l)) throw new Error("PIN");
      await importWallet(token, { pinDigits: d, pinLetters: l });
      toast.ok("ایمپورت");
      setPinDigits("");
      setPinLetters("");
      setForm("none");
      await refresh();
    } catch (e) {
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onIrrCredited(balanceIrr: number) {
    if (balanceIrr > 0) setIrrRaw(balanceIrr);
    else void refresh();
    setShowIrrCtoc(false);
  }

  if (!session || !token) {
    return (
      <section className="panel">
        <p className="muted">—</p>
      </section>
    );
  }

  return (
    <section className="panel wallets-panel wx-wallet" dir="rtl">
      <div className="wx-wallet-value">
        <div className="row-between">
          <span className="muted">ارزش دارایی</span>
          <button type="button" className="wx-eye" onClick={() => setHidden((h) => !h)} aria-label="مخفی">
            {hidden ? "◐" : "◉"}
          </button>
        </div>
        <div className="wx-total ltr">
          {displayTotal}
          <span className="wx-total-unit">{unit === "irt" ? "تومان" : "USDT"}</span>
        </div>
        <div className="wx-mode-tabs">
          <ChipTab active={unit === "irt"} onClick={() => setUnit("irt")}>
            تومان
          </ChipTab>
          <ChipTab active={unit === "usdt"} onClick={() => setUnit("usdt")}>
            تتر
          </ChipTab>
        </div>
      </div>

      <div className="wx-circle-actions">
        <button type="button" className="wx-circle" onClick={() => setFlow("deposit")}>
          <span className="wx-circle-ico">↓</span>
          واریز
        </button>
        <button type="button" className="wx-circle" onClick={() => setFlow("withdraw")}>
          <span className="wx-circle-ico">↑</span>
          برداشت
        </button>
        <button type="button" className="wx-circle" onClick={() => onJump?.("trade")}>
          <span className="wx-circle-ico">⇄</span>
          معامله
        </button>
        <button type="button" className="wx-circle" onClick={() => void refresh()} disabled={loading}>
          <span className="wx-circle-ico">↻</span>
          تاریخچه
        </button>
        <button type="button" className="wx-circle" onClick={() => setForm(form === "create" ? "none" : "create")}>
          <span className="wx-circle-ico">+</span>
          آدرس‌ها
        </button>
      </div>

      {flow === "deposit" && (
        <DepositCoin
          token={token}
          wallets={items}
          onClose={() => setFlow("none")}
          onIrr={() => {
            setFlow("none");
            setShowIrrCtoc(true);
          }}
        />
      )}
      {flow === "withdraw" && (
        <WithdrawCoin
          token={token}
          wallets={items}
          usdtBalance={usdtRaw}
          onClose={() => setFlow("none")}
          onDone={() => void refresh()}
        />
      )}

      {showIrrCtoc && (
        <IrrCtocDeposit
          token={token}
          onClose={() => setShowIrrCtoc(false)}
          onCredited={onIrrCredited}
        />
      )}

      {form !== "none" && (
        <div className="card pin-card">
          {form === "create" && (
            <label>
              برچسب
              <input className="ltr" value={label} onChange={(e) => setLabel(e.target.value)} />
            </label>
          )}
          <label>
            PIN ۱۶ رقم
            <input
              className="ltr"
              inputMode="numeric"
              maxLength={16}
              value={pinDigits}
              onChange={(e) => setPinDigits(normalizePinDigits(e.target.value).slice(0, 16))}
            />
          </label>
          <label>
            PIN ۱۶ حرف
            <input
              className="ltr"
              maxLength={16}
              value={pinLetters}
              onChange={(e) => setPinLetters(normalizePinLetters(e.target.value).slice(0, 16))}
            />
          </label>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => void (form === "create" ? onCreate() : onImport())}
          >
            {busy ? "…" : form === "create" ? "ساخت" : "ایمپورت"}
          </button>
        </div>
      )}

      <div className="wx-section">
        <div className="row-between">
          <span className="wx-section-title" style={{ margin: 0 }}>
            دارایی‌های شما
          </span>
          <label className="wx-dust">
            <input type="checkbox" checked={hideDust} onChange={(e) => setHideDust(e.target.checked)} />
            بدون خرد
          </label>
        </div>
        <div className="wx-asset-list">
          {loading && <p className="muted">…</p>}
          {!loading && assets.length === 0 && <p className="muted">۰</p>}
          {assets.map((a) => (
            <article key={a.key} className="wx-asset-row">
              <div className="wx-asset-left">
                <span className="wx-token-dot sm">{a.icon}</span>
                <div>
                  <b>{a.name}</b>
                  <span className="muted ltr">{hidden ? "••••" : a.qty}</span>
                </div>
              </div>
              <b className="ltr">
                {hidden
                  ? "••••"
                  : unit === "irt" && fxIrt > 0
                    ? Math.round(a.valueUsd * fxIrt).toLocaleString("en-US")
                    : a.valueUsd > 0
                      ? `$${fmtUsd(a.valueUsd)}`
                      : "—"}
              </b>
            </article>
          ))}
        </div>
      </div>

      <div className="wallet-grid" style={{ marginTop: 12 }}>
        {items.map((w) => {
          const chain = String(w.chain).toLowerCase();
          const nmc = chain === "nmc" ? nmcMap[w.address.toLowerCase()] : null;
          const accountScan =
            nmc?.scanner?.account ||
            nmc?.scanner?.nmc_account ||
            (chain === "nmc" ? scannerAddressUrl(w.address) : null);
          return (
            <article key={`${w.id}-${w.chain}`} className="wallet-card">
              <div className="row-between">
                <b>{w.label || w.chain}</b>
                <span className="chip-mini">{w.chain}</span>
              </div>
              <p className="addr ltr">{hidden ? "••••••••" : w.address}</p>
              {accountScan && (
                <a className="scan-link ltr" href={accountScan} target="_blank" rel="noopener noreferrer">
                  scanner
                </a>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
